import * as Sentry from '@sentry/react';
import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { useClientPortalAuth } from '../../context/ClientPortalAuthContext';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { toast } from 'sonner';
import {
  FileText,
  Gauge,
  ChevronRight,
  ChevronLeft,
  Building2,
  LogOut,
  Loader2,
  AlertCircle,
  CheckCircle,
  Clock,
  LayoutDashboard,
  Menu,
  X,
  Printer,
  FileSignature,
  Ticket,
  Phone,
  Wifi,
  WifiOff,
  TrendingUp,
  Send,
  Sparkles,
  ArrowRight,
  Activity,
  History,
  FolderOpen,
  Ban,
  CreditCard,
  Euro,
  AlertTriangle,
  Eye,
} from 'lucide-react';
import { cn } from '../../lib/utils';
import SmartAds from '../../components/portal/SmartAds';

const LOGO_FALLBACK = "/logo-ldws.png";

// ==================== BOTTOM NAVIGATION BAR (Mobile Only) ====================
const PortalBottomNav = () => {
  const location = useLocation();
  const navigate = useNavigate();
  
  const navItems = [
    { path: '/espace-client/dashboard', icon: LayoutDashboard, label: 'Accueil' },
    { path: '/espace-client/contrats', icon: FileSignature, label: 'Contrats' },
    { path: '/espace-client/equipements', icon: Printer, label: 'Équipements' },
    { path: '/espace-client/tickets', icon: Ticket, label: 'Support' },
  ];

  const isActive = (path) => {
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  return (
    <nav 
      className="fixed bottom-0 left-0 right-0 z-[9999] lg:hidden bg-white border-t border-slate-200 shadow-[0_-4px_20px_rgba(0,0,0,0.08)]"
      style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
      data-testid="portal-bottom-nav"
    >
      <div className="flex items-stretch justify-around h-16 max-w-screen-sm mx-auto">
        {navItems.map((item) => {
          const active = isActive(item.path);
          return (
            <button
              key={item.path}
              onClick={() => navigate(item.path)}
              data-testid={`bottom-nav-${item.path.split('/').pop()}`}
              className={cn(
                "flex-1 flex flex-col items-center justify-center gap-1 transition-all duration-200 relative",
                active 
                  ? "text-blue-600" 
                  : "text-slate-500 hover:text-slate-700 active:bg-slate-50"
              )}
            >
              {/* Active indicator */}
              {active && (
                <div className="absolute top-0 left-1/2 -translate-x-1/2 w-12 h-1 bg-blue-600 rounded-b-full" />
              )}
              <item.icon className={cn(
                "w-5 h-5 transition-transform duration-200",
                active && "scale-110"
              )} />
              <span className={cn(
                "text-[10px] font-medium leading-none",
                active && "font-semibold"
              )}>
                {item.label}
              </span>
            </button>
          );
        })}
      </div>
    </nav>
  );
};

// ==================== SIDEBAR COMPONENT ====================
const PortalSidebar = ({ collapsed, setCollapsed, selectedClientId, clients, onClientChange, mobileOpen, onMobileClose, logoUrl = LOGO_FALLBACK, hasMpsContract = true, pendingCountersCount = 0 }) => {
  const location = useLocation();

  const navItems = [
    { path: '/espace-client/dashboard', icon: LayoutDashboard, label: 'Tableau de bord' },
    { path: '/espace-client/equipements', icon: Printer, label: 'Mes Équipements' },
    { path: '/espace-client/contrats', icon: FileSignature, label: 'Mes Contrats' },
    ...(hasMpsContract ? [{ path: '/espace-client/compteurs', icon: Gauge, label: 'Compteurs', badge: pendingCountersCount > 0 ? pendingCountersCount : null }] : []),
    { path: '/espace-client/factures', icon: FileText, label: 'Factures' },
    { path: '/espace-client/documents', icon: FolderOpen, label: 'Documents' },
    { path: '/espace-client/tickets', icon: Ticket, label: 'Support' },
  ];

  // Handle navigation on mobile - close menu after click
  const handleNavClick = () => {
    if (mobileOpen) {
      onMobileClose();
    }
  };

  return (
    <>
      {/* Mobile overlay */}
      {mobileOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={onMobileClose}
        />
      )}
      
      <aside className={cn(
        "fixed left-0 top-0 h-screen bg-gradient-to-b from-slate-900 to-slate-800 text-white z-50 transition-all duration-300 shadow-xl",
        // Desktop: normal sidebar
        "hidden lg:block",
        collapsed ? "lg:w-20" : "lg:w-72"
      )}>
        {/* Header with Logo */}
        <div className="h-20 flex items-center justify-between px-4 border-b border-slate-700/50">
          {!collapsed && (
            <div className="flex items-center gap-3">
              <img 
                src={logoUrl} 
                alt="LDWS Groupe" 
                className="h-8 object-contain brightness-0 invert"
              />
              <div className="text-xs text-slate-400 leading-tight">
                Espace<br/>Client
              </div>
            </div>
          )}
          <button
            onClick={() => setCollapsed(!collapsed)}
            className="p-2.5 rounded-xl hover:bg-white/10 transition-colors"
          >
            {collapsed ? <Menu className="w-5 h-5" /> : <ChevronLeft className="w-5 h-5" />}
          </button>
        </div>

        {/* Client Selector - Multi-Company */}
        {!collapsed && clients.length > 0 && (
          <div className="p-4 border-b border-slate-700/50">
            <div className="text-xs text-slate-400 mb-2 uppercase tracking-wider font-medium">Société</div>
            {clients.length > 1 ? (
              <Select value={selectedClientId} onValueChange={onClientChange}>
                <SelectTrigger className="w-full bg-gradient-to-r from-slate-800 to-slate-700 border-slate-600 text-white hover:from-slate-700 hover:to-slate-600 transition-all" data-testid="portal-client-selector">
                  <div className="flex items-center gap-2 min-w-0">
                    <Building2 className="w-4 h-4 text-blue-400 flex-shrink-0" />
                    <span className="truncate"><SelectValue placeholder="Sélectionner" /></span>
                  </div>
                </SelectTrigger>
                <SelectContent className="max-h-[300px]">
                  {clients.map((client) => (
                    <SelectItem key={client.id} value={client.id} className="cursor-pointer">
                      <span className="truncate">{client.raison_sociale}</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : (
              <div className="px-3 py-2.5 rounded-lg bg-gradient-to-r from-slate-800 to-slate-700 border border-slate-600">
                <div className="flex items-center gap-2 min-w-0">
                  <Building2 className="w-4 h-4 text-blue-400 flex-shrink-0" />
                  <span className="text-sm font-medium text-white truncate">{clients[0]?.raison_sociale}</span>
                </div>
              </div>
            )}
            {clients.length > 1 && (
              <p className="text-xs text-slate-500 mt-2 text-center">
                {clients.length} sociétés disponibles
              </p>
            )}
          </div>
        )}

        {/* Navigation */}
        <nav className="p-3 space-y-1 flex-1">
          {navItems.map((item) => {
            const isActive = location.pathname === item.path || location.pathname.startsWith(item.path + '/');
            return (
              <Link
                key={item.path}
                to={item.path}
                onClick={handleNavClick}
                data-testid={`portal-nav-${item.path.split('/').pop()}`}
                className={cn(
                  "relative flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-200",
                  isActive
                    ? "bg-gradient-to-r from-blue-600 to-blue-500 text-white shadow-lg shadow-blue-500/25"
                    : "text-slate-300 hover:bg-white/10 hover:text-white"
                )}
              >
                <div className="relative flex-shrink-0">
                  <item.icon className={cn("w-5 h-5", isActive && "drop-shadow")} />
                  {item.badge && collapsed && (
                    <span className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center ring-2 ring-slate-900">
                      {item.badge > 9 ? '9+' : item.badge}
                    </span>
                  )}
                </div>
                {!collapsed && (
                  <>
                    <span className="font-medium text-sm">{item.label}</span>
                    {item.badge && (
                      <span className="ml-auto min-w-[22px] h-[22px] px-1.5 rounded-full bg-red-500 text-white text-[11px] font-bold flex items-center justify-center shadow-sm">
                        {item.badge > 9 ? '9+' : item.badge}
                      </span>
                    )}
                  </>
                )}
              </Link>
          );
        })}
      </nav>

      {/* Footer */}
      {!collapsed && (
        <div className="p-4 border-t border-slate-700/50">
          <div className="text-xs text-slate-500 text-center">
            Powered by LDWS Groupe
          </div>
        </div>
      )}
    </aside>
    
    {/* Mobile Sidebar - Slide-in from left */}
    <aside className={cn(
      "fixed left-0 top-0 h-screen w-72 bg-gradient-to-b from-slate-900 to-slate-800 text-white z-50 transition-transform duration-300 shadow-xl lg:hidden",
      mobileOpen ? "translate-x-0" : "-translate-x-full"
    )}>
      {/* Header with Logo */}
      <div className="h-16 flex items-center justify-between px-4 border-b border-slate-700/50">
        <div className="flex items-center gap-3">
          <img 
            src={logoUrl} 
            alt="LDWS Groupe" 
            className="h-7 object-contain brightness-0 invert"
          />
          <div className="text-xs text-slate-400 leading-tight">
            Espace<br/>Client
          </div>
        </div>
        <button
          onClick={onMobileClose}
          className="p-2 rounded-xl hover:bg-white/10 transition-colors"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      {/* Client Selector - Multi-Company */}
      {clients.length > 0 && (
        <div className="p-4 border-b border-slate-700/50">
          <div className="text-xs text-slate-400 mb-2 uppercase tracking-wider font-medium">Société</div>
          {clients.length > 1 ? (
            <Select value={selectedClientId} onValueChange={(val) => { onClientChange(val); onMobileClose(); }}>
              <SelectTrigger className="w-full bg-gradient-to-r from-slate-800 to-slate-700 border-slate-600 text-white hover:from-slate-700 hover:to-slate-600 transition-all">
                <div className="flex items-center gap-2">
                  <Building2 className="w-4 h-4 text-blue-400" />
                  <SelectValue placeholder="Sélectionner" />
                </div>
              </SelectTrigger>
              <SelectContent className="max-h-[300px]">
                {clients.map((client) => (
                  <SelectItem key={client.id} value={client.id} className="cursor-pointer">
                    <span>{client.raison_sociale}</span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : (
            <div className="px-3 py-2.5 rounded-lg bg-gradient-to-r from-slate-800 to-slate-700 border border-slate-600">
              <div className="flex items-center gap-2">
                <Building2 className="w-4 h-4 text-blue-400" />
                <span className="text-sm font-medium text-white truncate">{clients[0]?.raison_sociale}</span>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Navigation */}
      <nav className="p-3 space-y-1 flex-1 overflow-y-auto">
        {navItems.map((item) => {
          const isActive = location.pathname === item.path || location.pathname.startsWith(item.path + '/');
          return (
            <Link
              key={item.path}
              to={item.path}
              onClick={handleNavClick}
              className={cn(
                "flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-200",
                isActive 
                  ? "bg-gradient-to-r from-blue-600 to-blue-500 text-white shadow-lg shadow-blue-500/25" 
                  : "text-slate-300 hover:bg-white/10 hover:text-white"
              )}
            >
              <item.icon className={cn("w-5 h-5 flex-shrink-0", isActive && "drop-shadow")} />
              <span className="font-medium text-sm">{item.label}</span>
            </Link>
          );
        })}
      </nav>

      {/* Footer */}
      <div className="p-4 border-t border-slate-700/50">
        <div className="text-xs text-slate-500 text-center">
          Powered by LDWS Groupe
        </div>
      </div>
    </aside>
    </>
  );
};

// ==================== PROMO BANNER COMPONENT ====================
const PromoBanner = ({ promo, onCallbackClick, onDismiss }) => {
  if (!promo) return null;
  
  // Check if this is a Callwork promo to use custom branding
  const isCallwork = promo.title?.toLowerCase().includes('callwork');
  
  if (isCallwork) {
    return (
      <div className="relative overflow-hidden rounded-2xl shadow-lg" style={{ background: 'linear-gradient(135deg, #fcf5ea 0%, #f8f7ff 100%)' }}>
        {/* Close button */}
        <button
          onClick={(e) => { e.stopPropagation(); onDismiss && onDismiss(); }}
          className="absolute top-3 right-3 z-10 w-8 h-8 rounded-full bg-white/80 hover:bg-white flex items-center justify-center text-slate-400 hover:text-slate-600 transition-all shadow-sm"
          aria-label="Fermer"
          data-testid="banner-dismiss-btn"
        >
          <X className="w-4 h-4" />
        </button>
        
        <div className="p-4 sm:p-6 flex flex-col sm:flex-row items-center gap-6">
          {/* Left side - GIF Image */}
          <div className="w-32 h-32 sm:w-40 sm:h-40 flex-shrink-0">
            <img 
              src="/callwork.gif"
              alt="Callwork"
              className="w-full h-full object-contain"
            />
          </div>
          
          {/* Center - Content */}
          <div className="flex-1 text-center sm:text-left">
            <div className="flex items-center justify-center sm:justify-start gap-2 mb-2">
              <span className="text-xs font-semibold uppercase tracking-wider px-3 py-1 rounded-full" style={{ backgroundColor: '#02a96d', color: 'white' }}>
                Partenaire
              </span>
            </div>
            <h3 className="text-2xl sm:text-3xl font-bold text-slate-800 mb-2" style={{ fontFamily: 'Sora, sans-serif' }}>
              Callwork
            </h3>
            <p className="text-base sm:text-lg text-slate-700 font-medium mb-1">
              Téléphonie Pro Simplifiée
            </p>
            <p className="text-sm text-slate-600 max-w-lg">
              Vos équipes appellent et répondent partout. Appels illimités, app mobile incluse, support France réactif.
            </p>
          </div>
          
          {/* Right side - CTAs */}
          <div className="flex-shrink-0 flex flex-col sm:flex-row gap-3">
            <a 
              href="https://callwork.co/" 
              target="_blank" 
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 px-6 py-3 font-semibold rounded-xl transition-all hover:scale-105 shadow-lg text-base"
              style={{ backgroundColor: '#02a96d', color: 'white' }}
              data-testid="promo-cta-btn"
            >
              Découvrir Callwork
              <ArrowRight className="w-5 h-5" />
            </a>
            <a 
              href="https://tu9asy65pqw.typeform.com/to/ny2j8mNP" 
              target="_blank" 
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 px-6 py-3 font-semibold rounded-xl transition-all hover:scale-105 shadow-md text-base border-2"
              style={{ backgroundColor: 'white', color: '#02a96d', borderColor: '#02a96d' }}
              data-testid="promo-callback-btn"
            >
              Se faire rappeler
              <Phone className="w-5 h-5" />
            </a>
          </div>
        </div>
      </div>
    );
  }
  
  // Default promo banner for other promos
  return (
    <div 
      className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-blue-600 via-blue-500 to-cyan-500 text-white shadow-lg"
      style={promo.image_url ? { backgroundImage: `url(${promo.image_url})`, backgroundSize: 'cover', backgroundPosition: 'center' } : {}}
    >
      <div className={cn("p-6", promo.image_url && "bg-gradient-to-r from-blue-600/90 to-transparent")}>
        <div className="flex items-center justify-between">
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-yellow-300" />
              <span className="text-xs font-semibold uppercase tracking-wider text-blue-100">
                {promo.placement === 'banner' ? 'Offre spéciale' : 'Découvrez'}
              </span>
            </div>
            <h3 className="text-xl font-bold">{promo.title}</h3>
            {promo.description && (
              <p className="text-sm text-blue-100 max-w-md">{promo.description}</p>
            )}
          </div>
          {promo.cta_action !== 'none' && (
            <Button 
              onClick={() => promo.cta_action === 'callback' ? onCallbackClick(promo) : window.open(promo.cta_link, '_blank')}
              className="bg-white text-blue-600 hover:bg-blue-50 shadow-lg"
              data-testid="promo-cta-btn"
            >
              {promo.cta_text || 'En savoir plus'}
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

// ==================== CALLBACK MODAL ====================
const CallbackModal = ({ open, onClose, contact, client, portalApi, promoSource }) => {
  const [loading, setLoading] = useState(false);
  const [phone, setPhone] = useState(contact?.telephone || '');
  const [message, setMessage] = useState('');

  const handleSubmit = async () => {
    setLoading(true);
    try {
      await portalApi.post('/client-portal/callback-request', {
        telephone: phone,
        message,
        promo_source: promoSource
      });
      toast.success('Demande de rappel envoyée !', {
        description: 'Nous vous contacterons dans les plus brefs délais.'
      });
      onClose();
    } catch (error) {
      toast.error('Erreur lors de l\'envoi');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Phone className="w-5 h-5 text-blue-600" />
            Demande de rappel
          </DialogTitle>
          <DialogDescription>
            Remplissez ce formulaire et nous vous rappellerons rapidement
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-muted-foreground">Prénom</Label>
              <Input value={contact?.prenom || ''} disabled className="bg-muted" />
            </div>
            <div className="space-y-2">
              <Label className="text-muted-foreground">Nom</Label>
              <Input value={contact?.nom || ''} disabled className="bg-muted" />
            </div>
          </div>
          
          <div className="space-y-2">
            <Label className="text-muted-foreground">Email</Label>
            <Input value={contact?.email || ''} disabled className="bg-muted" />
          </div>
          
          <div className="space-y-2">
            <Label className="text-muted-foreground">Société</Label>
            <Input value={client?.raison_sociale || ''} disabled className="bg-muted" />
          </div>
          
          <div className="space-y-2">
            <Label>Téléphone *</Label>
            <Input 
              value={phone} 
              onChange={(e) => setPhone(e.target.value)}
              placeholder="06 12 34 56 78"
              data-testid="callback-phone-input"
            />
          </div>
          
          <div className="space-y-2">
            <Label>Message (optionnel)</Label>
            <Textarea 
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Précisez votre demande..."
              rows={3}
              data-testid="callback-message-input"
            />
          </div>
        </div>
        
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Annuler</Button>
          <Button 
            onClick={handleSubmit} 
            disabled={loading || !phone}
            data-testid="callback-submit-btn"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
            Envoyer
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ==================== LAYOUT WRAPPER ====================
export const PortalLayout = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const isDashboardRoute = location.pathname === '/espace-client/dashboard'
    || location.pathname === '/espace-client';
  const { contact, logout, portalApi, isImpersonation } = useClientPortalAuth();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [clients, setClients] = useState([]);
  // Initialize from localStorage if available
  const [selectedClientId, setSelectedClientId] = useState(() => {
    return localStorage.getItem('portal_selected_client_id') || null;
  });
  const [promos, setPromos] = useState([]);
  const [callbackModalOpen, setCallbackModalOpen] = useState(false);
  const [callbackPromoSource, setCallbackPromoSource] = useState(null);
  const [bannerDismissed, setBannerDismissed] = useState(false);
  // Dynamic logo from Paramètres → Identité visuelle
  const [logoUrl, setLogoUrl] = useState(LOGO_FALLBACK);

  // Suspension state
  const [suspensionStatus, setSuspensionStatus] = useState(null);

  // MPS contract flag — drives visibility of "Compteurs" sidebar entry
  const [hasMpsContract, setHasMpsContract] = useState(false);
  // Pending counters count — drives the notification badge on the "Compteurs" sidebar entry
  const [pendingCountersCount, setPendingCountersCount] = useState(0);

  // Persist selectedClientId to localStorage
  useEffect(() => {
    if (selectedClientId) {
      localStorage.setItem('portal_selected_client_id', selectedClientId);
    }
  }, [selectedClientId]);

  useEffect(() => {
    fetchClients();
    fetchPromos();
    // Fetch company branding (logo) from settings — same endpoint as
    // the login page uses, no auth required.
    portalApi.get('/settings/public/branding').then(res => {
      if (res.data?.logo_url) {
        setLogoUrl(`${process.env.REACT_APP_BACKEND_URL}${res.data.logo_url}`);
      }
    }).catch(() => {});
  }, []);

  // Fetch suspension status when client changes
  useEffect(() => {
    if (selectedClientId) {
      fetchSuspensionStatus();
      fetchMpsFlag();
      fetchPendingCounters();
    } else {
      setHasMpsContract(false);
      setPendingCountersCount(0);
    }
  }, [selectedClientId]);

  const fetchSuspensionStatus = async () => {
    if (!selectedClientId) return;
    try {
      const response = await portalApi.get(`/client-portal/clients/${selectedClientId}/suspension-status`);
      setSuspensionStatus(response.data);
    } catch (error) {
      setSuspensionStatus(null);
    }
  };

  const fetchMpsFlag = async () => {
    if (!selectedClientId) return;
    try {
      const response = await portalApi.get(`/client-portal/clients/${selectedClientId}/contracts`);
      const contracts = response.data?.contracts || [];
      const hasMps = contracts.some((c) => {
        const ts = (c.type_service || '').toLowerCase();
        const statut = (c.statut || '').toLowerCase();
        return statut !== 'resilie' && (ts.includes('mps') || ts.includes('psp'));
      });
      setHasMpsContract(hasMps);
    } catch (error) {
      setHasMpsContract(false);
    }
  };

  const fetchPendingCounters = async () => {
    if (!selectedClientId) return;
    try {
      const response = await portalApi.get(`/client-portal/clients/${selectedClientId}/counters/pending`);
      setPendingCountersCount(response.data?.total_pending || 0);
    } catch (error) {
      setPendingCountersCount(0);
    }
  };

  const fetchClients = async () => {
    try {
      const response = await portalApi.get('/client-portal/clients');
      const clientsList = response.data.clients || [];
      setClients(clientsList);
      
      // Only set default if no valid selection
      if (clientsList.length > 0) {
        const savedClientId = localStorage.getItem('portal_selected_client_id');
        const isValidSavedClient = savedClientId && clientsList.some(c => c.id === savedClientId);
        
        if (!selectedClientId || !clientsList.some(c => c.id === selectedClientId)) {
          setSelectedClientId(isValidSavedClient ? savedClientId : clientsList[0].id);
        }
      }
    } catch (error) {
      Sentry.captureException(error);
    }
  };

  const fetchPromos = async () => {
    try {
      const response = await portalApi.get('/client-portal/promos');
      setPromos(response.data.promos || []);
    } catch (error) {
      Sentry.captureException(error);
    }
  };

  const handleLogout = () => {
    // Clear persisted client selection
    localStorage.removeItem('portal_selected_client_id');
    logout();
    navigate('/espace-client/login');
  };

  const handleCallbackClick = (promo) => {
    setCallbackPromoSource(promo?.title || 'direct');
    setCallbackModalOpen(true);
  };

  const selectedClient = clients.find(c => c.id === selectedClientId);
  const bannerPromo = promos.find(p => p.placement === 'banner');

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      <PortalSidebar
        collapsed={collapsed}
        setCollapsed={setCollapsed}
        selectedClientId={selectedClientId}
        clients={clients}
        onClientChange={setSelectedClientId}
        mobileOpen={mobileMenuOpen}
        onMobileClose={() => setMobileMenuOpen(false)}
        logoUrl={logoUrl}
        hasMpsContract={hasMpsContract}
        pendingCountersCount={pendingCountersCount}
      />
      
      {/* Main content */}
      <div className={cn(
        "transition-all duration-300",
        // Desktop: margin for sidebar
        collapsed ? "lg:ml-20" : "lg:ml-72",
        // Mobile: no margin, but add bottom padding for bottom nav
        "ml-0 pb-20 lg:pb-0"
      )}>
        {/* Top bar */}
        <header className="h-16 lg:h-20 bg-white/80 backdrop-blur-lg border-b border-slate-200/50 sticky top-0 z-30 flex items-center justify-between px-4 lg:px-8">
          {/* Mobile: Hamburger + Logo */}
          <div className="flex items-center gap-3">
            <button
              onClick={() => setMobileMenuOpen(true)}
              className="lg:hidden p-2 -ml-2 rounded-lg hover:bg-slate-100 transition-colors"
              data-testid="mobile-menu-btn"
            >
              <Menu className="w-6 h-6 text-slate-600" />
            </button>
            
            {/* Mobile Logo */}
            <div className="lg:hidden flex items-center gap-2">
              <img 
                src={logoUrl} 
                alt="LDWS" 
                className="h-7 object-contain"
              />
            </div>
            
            {/* Desktop: Client info */}
            <div className="hidden lg:block">
              {selectedClient && (
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center shadow-lg shadow-blue-500/20">
                    <Building2 className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <div className="font-semibold text-slate-900">{selectedClient.raison_sociale}</div>
                    {selectedClient.siret && (
                      <div className="text-xs text-slate-500">SIRET: {selectedClient.siret}</div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
          
          <div className="flex items-center gap-2 lg:gap-4">
            {/* Quick callback button - links to HubSpot meeting */}
            <a 
              href="https://meetings-eu1.hubspot.com/ld-ws"
              target="_blank"
              rel="noopener noreferrer"
              className="hidden md:flex items-center gap-2 px-3 py-2 text-sm font-medium border border-blue-200 text-blue-600 hover:bg-blue-50 rounded-md transition-colors"
              data-testid="quick-callback-btn"
            >
              <Phone className="w-4 h-4" />
              Être rappelé
            </a>
            
            <div className="hidden md:block h-8 w-px bg-slate-200" />
            
            <div className="flex items-center gap-2 lg:gap-3">
              <div className="w-8 h-8 lg:w-9 lg:h-9 rounded-full bg-gradient-to-br from-slate-600 to-slate-700 flex items-center justify-center text-white text-xs lg:text-sm font-medium">
                {contact?.prenom?.[0]}{contact?.nom?.[0]}
              </div>
              <div className="hidden md:block">
                <div className="text-sm font-medium text-slate-900">
                  {contact?.prenom} {contact?.nom}
                </div>
                <div className="text-xs text-slate-500">{contact?.email}</div>
              </div>
            </div>
            
            <Button 
              variant="ghost" 
              size="icon" 
              onClick={handleLogout}
              className="text-slate-500 hover:text-slate-900 hover:bg-slate-100"
              data-testid="portal-logout-btn"
            >
              <LogOut className="w-5 h-5" />
            </Button>
          </div>
        </header>

        {/* Page content */}
        <main className="p-4 lg:p-8 pb-24 lg:pb-8">
          {/* Admin impersonation banner */}
          {isImpersonation && (
            <div className="mb-4 bg-purple-600 text-white rounded-xl p-3 flex items-center justify-between shadow-md" data-testid="impersonation-banner">
              <div className="flex items-center gap-3">
                <div className="p-1.5 bg-white/20 rounded-full">
                  <Eye className="w-4 h-4" />
                </div>
                <p className="text-sm font-medium">
                  Mode admin — vous consultez le portail de <span className="font-bold">{selectedClient?.raison_sociale || 'ce client'}</span> en lecture seule
                </p>
              </div>
              <button
                onClick={() => { logout(); window.close(); }}
                className="text-sm bg-white/20 hover:bg-white/30 px-3 py-1 rounded-md font-medium transition-colors"
              >
                Quitter
              </button>
            </div>
          )}
          {/* Suspension Alert Banner */}
          {suspensionStatus?.is_suspended && (
            <div className="mb-6 bg-red-600 text-white rounded-xl shadow-lg overflow-hidden" data-testid="suspension-banner">
              <div className="p-6">
                <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                  <div className="flex items-start gap-4">
                    <div className="p-3 bg-white/20 rounded-full">
                      <Ban className="w-8 h-8" />
                    </div>
                    <div>
                      <h2 className="text-xl font-bold flex items-center gap-2">
                        <AlertTriangle className="w-5 h-5" />
                        Vos services sont suspendus
                      </h2>
                      <p className="text-red-100 mt-1">
                        En raison d'un impayé, l'accès à certaines fonctionnalités a été restreint.
                      </p>
                    </div>
                  </div>
                  <div className="flex flex-col items-center bg-white/10 rounded-xl p-4">
                    <span className="text-sm text-red-100">Montant à régler</span>
                    <span className="text-3xl font-bold flex items-center gap-1">
                      <Euro className="w-6 h-6" />
                      {suspensionStatus.suspension?.amount_due?.toFixed(2) || '0.00'}
                    </span>
                  </div>
                </div>
                
                <div className="mt-6 pt-6 border-t border-white/20">
                  <p className="text-sm text-red-100 mb-4">Régularisez votre situation pour rétablir vos services :</p>
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    {/* Stripe Payment Link */}
                    {suspensionStatus.suspension?.payment_link && (
                      <a
                        href={suspensionStatus.suspension.payment_link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center justify-center gap-3 bg-white text-red-600 py-4 px-6 rounded-xl font-semibold hover:bg-red-50 transition-colors shadow-lg"
                        data-testid="pay-now-btn"
                      >
                        <CreditCard className="w-5 h-5" />
                        Payer maintenant par carte bancaire
                      </a>
                    )}
                    
                    {/* Bank Transfer Info */}
                    <div className="bg-white/10 rounded-xl p-4 text-sm">
                      <p className="font-medium mb-2">Virement bancaire :</p>
                      <p className="text-red-100">
                        IBAN : <span className="font-mono text-white">{suspensionStatus.suspension?.bank_iban || 'Non disponible'}</span>
                      </p>
                      <p className="text-red-100">
                        BIC : <span className="font-mono text-white">{suspensionStatus.suspension?.bank_bic || 'Non disponible'}</span>
                      </p>
                      {suspensionStatus.suspension?.payment_reference && (
                        <p className="text-red-100 mt-2">
                          Référence : <span className="font-mono text-white font-bold">{suspensionStatus.suspension.payment_reference}</span>
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
          
          {/* Banner Promo (admin-configured) */}
          {bannerPromo && !bannerDismissed && (
            <div className="mb-8">
              <PromoBanner
                promo={bannerPromo}
                onCallbackClick={handleCallbackClick}
                onDismiss={() => setBannerDismissed(true)}
              />
            </div>
          )}

          {React.cloneElement(children, {
            selectedClientId,
            selectedClient,
            clients,
            setSelectedClientId,
            promos: promos.filter(p => p.placement !== 'banner'),
            onCallbackClick: handleCallbackClick,
            isSuspended: suspensionStatus?.is_suspended || false,
            suspensionStatus,
          })}

          {/* Cross-sell ads — dashboard route only. Main banner first, then
              the two smaller ads underneath (all grouped at the bottom). */}
          {selectedClientId && isDashboardRoute && (
            <div className="mt-8 space-y-4">
              <SmartAds clientId={selectedClientId} placement="dashboard-main" />
              <SmartAds clientId={selectedClientId} placement="dashboard-secondary" />
            </div>
          )}
        </main>
      </div>

      {/* Callback Modal */}
      <CallbackModal 
        open={callbackModalOpen}
        onClose={() => setCallbackModalOpen(false)}
        contact={contact}
        client={selectedClient}
        portalApi={portalApi}
        promoSource={callbackPromoSource}
      />
      
      {/* Bottom Navigation Bar - Mobile Only */}
      <PortalBottomNav />
    </div>
  );
};

// ==================== DASHBOARD PAGE ====================
export const PortalDashboardPage = ({ selectedClientId, selectedClient, promos, onCallbackClick }) => {
  const navigate = useNavigate();
  const { portalApi } = useClientPortalAuth();
  
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    invoices: { total: 0, unpaid: 0, unpaidAmount: 0 },
    counters: { pending: 0, total: 0 },
    equipments: { total: 0, connected: 0, disconnected: 0 },
    tickets: { open: 0, total: 0 },
    contracts: { total: 0, hasMps: false, hasUcaas: false, hasItaas: false, hasDraas: false },
  });

  useEffect(() => {
    if (selectedClientId) {
      fetchAllData(selectedClientId);
    }
  }, [selectedClientId]);

  const fetchAllData = async (clientId) => {
    setLoading(true);
    try {
      const [invoicesRes, countersRes, equipmentsRes, ticketsRes, contractsRes] = await Promise.all([
        portalApi.get(`/client-portal/clients/${clientId}/invoices`),
        portalApi.get(`/client-portal/clients/${clientId}/counters/pending`),
        portalApi.get(`/client-portal/clients/${clientId}/equipments`),
        portalApi.get(`/client-portal/clients/${clientId}/tickets`),
        portalApi.get(`/client-portal/clients/${clientId}/contracts`).catch(() => ({ data: { contracts: [] } })),
      ]);

      const invoices = invoicesRes.data.invoices || [];
      const unpaidInvoices = invoices.filter(inv =>
        ['en_attente', 'impaye'].includes(inv.statut) || inv.statut_paiement === 'en_attente'
      );

      const equipments = equipmentsRes.data.equipments || [];
      const connected = equipments.filter(e => e.kpax_status === 'compatible_connecte').length;
      const disconnected = equipments.filter(e => e.kpax_status === 'compatible_deconnecte').length;

      const tickets = ticketsRes.data.tickets || [];
      const openTickets = tickets.filter(t => ['ouvert', 'en_cours'].includes(t.statut)).length;

      const contracts = contractsRes.data?.contracts || [];
      const activeContracts = contracts.filter(c => (c.statut || '').toLowerCase() !== 'resilie');
      const hasType = (needle) => activeContracts.some(c => (c.type_service || '').toLowerCase().includes(needle));
      const hasMps = hasType('mps') || hasType('psp');

      setStats({
        invoices: {
          total: invoices.length,
          unpaid: unpaidInvoices.length,
          unpaidAmount: unpaidInvoices.reduce((sum, inv) => sum + (inv.montant_ttc || 0), 0)
        },
        counters: {
          pending: countersRes.data.total_pending || 0,
          total: equipments.length
        },
        equipments: {
          total: equipments.length,
          connected,
          disconnected
        },
        tickets: {
          open: openTickets,
          total: tickets.length
        },
        contracts: {
          total: activeContracts.length,
          hasMps,
          hasUcaas: hasType('ucaas'),
          hasItaas: hasType('itaas'),
          hasDraas: hasType('draas'),
        },
      });
    } catch (error) {
      Sentry.captureException(error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(value);
  };

  const sidebarPromo = promos?.find(p => p.placement === 'sidebar');

  if (loading) {
    return (
      <div className="flex justify-center py-20">
        <Loader2 className="w-10 h-10 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6 md:space-y-8 animate-fadeIn">
      {/* Welcome Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-slate-900">Tableau de bord</h1>
          <p className="text-base md:text-sm text-slate-500 mt-1">Bienvenue, {selectedClient?.raison_sociale}</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 md:gap-6 lg:gap-8">
        {/* Main Stats Grid */}
        <div className="lg:col-span-3 grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-6">
          {/* K-PAX Status Card */}
          <Card 
            className="cursor-pointer hover:shadow-lg transition-all duration-300 active:scale-[0.98] bg-white border border-slate-200 shadow-md overflow-hidden group rounded-2xl"
            onClick={() => navigate('/espace-client/equipements')}
            data-testid="dashboard-kpax-card"
          >
            <CardContent className="p-4 md:p-6">
              <div className="flex items-start mb-4">
                <div className="w-12 h-12 md:w-14 md:h-14 rounded-2xl bg-gradient-to-br from-emerald-400 to-emerald-600 flex items-center justify-center shadow-lg shadow-emerald-500/30">
                  <Activity className="w-6 h-6 md:w-7 md:h-7 text-white" />
                </div>
              </div>
              <div className="space-y-3">
                <div>
                  <p className="text-base md:text-sm text-slate-500">État de la flotte</p>
                  <p className="text-2xl md:text-3xl font-bold text-slate-900">{stats.equipments.total} machines</p>
                </div>
                <div className="flex flex-wrap items-center gap-3 md:gap-4">
                  <div className="flex items-center gap-2">
                    <Wifi className="w-5 h-5 text-emerald-500" />
                    <span className="text-base md:text-sm font-medium text-emerald-600">{stats.equipments.connected} connectées</span>
                  </div>
                  {stats.equipments.disconnected > 0 && (
                    <div className="flex items-center gap-2">
                      <WifiOff className="w-5 h-5 text-red-500" />
                      <span className="text-base md:text-sm font-medium text-red-600">{stats.equipments.disconnected} déconnectées</span>
                    </div>
                  )}
                </div>
              </div>
              <div className="mt-4 pt-4 border-t border-slate-100 flex items-center text-base md:text-sm text-slate-500 group-hover:text-blue-600 transition-colors min-h-[44px]">
                Voir mes équipements <ChevronRight className="w-5 h-5 ml-1 group-hover:translate-x-1 transition-transform" />
              </div>
            </CardContent>
          </Card>

          {/* Contracts Card — shown for every service (counters moved to a
              sidebar notification badge on the "Compteurs" entry). */}
          <Card
            className="cursor-pointer hover:shadow-lg transition-all duration-300 active:scale-[0.98] bg-white border border-slate-200 shadow-md overflow-hidden group rounded-2xl"
            onClick={() => navigate('/espace-client/contrats')}
            data-testid="dashboard-contracts-card"
          >
            <CardContent className="p-4 md:p-6">
              <div className="flex items-start mb-4">
                <div className="w-12 h-12 md:w-14 md:h-14 rounded-2xl bg-gradient-to-br from-indigo-400 to-indigo-600 flex items-center justify-center shadow-lg shadow-indigo-500/30">
                  <FileSignature className="w-6 h-6 md:w-7 md:h-7 text-white" />
                </div>
              </div>
              <div className="space-y-3">
                <div>
                  <p className="text-base md:text-sm text-slate-500">Mes contrats</p>
                  <p className="text-2xl md:text-3xl font-bold text-slate-900">
                    {stats.contracts.total} {stats.contracts.total > 1 ? 'contrats' : 'contrat'}
                  </p>
                  <p className="text-base md:text-sm text-slate-500">
                    {stats.contracts.total > 0 ? 'en cours' : 'aucun contrat actif'}
                  </p>
                </div>
              </div>
              <div className="mt-4 pt-4 border-t border-slate-100 flex items-center text-base md:text-sm text-slate-500 group-hover:text-indigo-600 transition-colors min-h-[44px]">
                Voir mes contrats <ChevronRight className="w-5 h-5 ml-1 group-hover:translate-x-1 transition-transform" />
              </div>
            </CardContent>
          </Card>

          {/* Invoices Card */}
          <Card 
            className="cursor-pointer hover:shadow-lg transition-all duration-300 active:scale-[0.98] bg-white border border-slate-200 shadow-md overflow-hidden group rounded-2xl"
            onClick={() => navigate('/espace-client/factures')}
            data-testid="dashboard-invoices-card"
          >
            <CardContent className="p-4 md:p-6">
              <div className="flex items-start justify-between mb-4">
                <div className="w-12 h-12 md:w-14 md:h-14 rounded-2xl bg-gradient-to-br from-blue-400 to-blue-600 flex items-center justify-center shadow-lg shadow-blue-500/30">
                  <FileText className="w-6 h-6 md:w-7 md:h-7 text-white" />
                </div>
              </div>
              <div className="space-y-3">
                <div>
                  <p className="text-base md:text-sm text-slate-500">Factures</p>
                  <p className="text-2xl md:text-3xl font-bold text-slate-900">{stats.invoices.total}</p>
                </div>
                {stats.invoices.unpaid > 0 ? (
                  <div className="flex flex-wrap items-center gap-2">
                    <Badge variant="destructive" className="text-sm py-1">
                      <AlertCircle className="w-4 h-4 mr-1" />
                      {stats.invoices.unpaid} impayée(s)
                    </Badge>
                    <span className="text-base md:text-sm font-semibold text-red-600">
                      {formatCurrency(stats.invoices.unpaidAmount)}
                    </span>
                  </div>
                ) : (
                  <Badge className="bg-emerald-50 text-emerald-600 border-emerald-200 text-sm py-1">
                    <CheckCircle className="w-4 h-4 mr-1" />
                    À jour
                  </Badge>
                )}
              </div>
              <div className="mt-4 pt-4 border-t border-slate-100 flex items-center text-base md:text-sm text-slate-500 group-hover:text-blue-600 transition-colors min-h-[44px]">
                Consulter mes factures <ChevronRight className="w-5 h-5 ml-1 group-hover:translate-x-1 transition-transform" />
              </div>
            </CardContent>
          </Card>

          {/* Tickets Card */}
          <Card 
            className="cursor-pointer hover:shadow-lg transition-all duration-300 active:scale-[0.98] bg-white border border-slate-200 shadow-md overflow-hidden group rounded-2xl"
            onClick={() => navigate('/espace-client/tickets')}
            data-testid="dashboard-tickets-card"
          >
            <CardContent className="p-4 md:p-6">
              <div className="flex items-start justify-between mb-4">
                <div className="w-12 h-12 md:w-14 md:h-14 rounded-2xl bg-gradient-to-br from-purple-400 to-purple-600 flex items-center justify-center shadow-lg shadow-purple-500/30">
                  <Ticket className="w-6 h-6 md:w-7 md:h-7 text-white" />
                </div>
              </div>
              <div className="space-y-3">
                <div>
                  <p className="text-base md:text-sm text-slate-500">Support technique</p>
                  <p className="text-2xl md:text-3xl font-bold text-slate-900">{stats.tickets.open}</p>
                  <p className="text-base md:text-sm text-slate-500">ticket(s) en cours</p>
                </div>
              </div>
              <div className="mt-4 pt-4 border-t border-slate-100 flex items-center text-base md:text-sm text-slate-500 group-hover:text-blue-600 transition-colors min-h-[44px]">
                Voir mes demandes <ChevronRight className="w-5 h-5 ml-1 group-hover:translate-x-1 transition-transform" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Sidebar with Promo — flex column so Quick Actions stretches to fill */}
        <div className="flex flex-col gap-4 md:gap-6">
          {/* Sidebar Promo */}
          {sidebarPromo && (
            <Card className="border-0 shadow-lg overflow-hidden rounded-2xl" style={{ background: 'linear-gradient(135deg, #e8e4ff 0%, #f0e6ff 35%, #ffe4f3 70%, #ffe8f5 100%)' }}>
              <CardContent className="p-5">
                {sidebarPromo.image_url && (
                  <div className="w-full h-44 mb-4 flex items-center justify-center">
                    <img 
                      src={sidebarPromo.image_url} 
                      alt={sidebarPromo.title}
                      className="h-full object-contain"
                    />
                  </div>
                )}
                {!sidebarPromo.image_url && (
                  <Sparkles className="w-8 h-8 text-purple-400 mb-4" />
                )}
                <h3 className="text-lg font-bold mb-2 text-slate-800">{sidebarPromo.title}</h3>
                {sidebarPromo.description && (
                  <p className="text-sm text-slate-600 mb-4">{sidebarPromo.description}</p>
                )}
                {sidebarPromo.cta_action !== 'none' && (
                  <Button 
                    onClick={() => sidebarPromo.cta_action === 'callback' ? onCallbackClick(sidebarPromo) : window.open(sidebarPromo.cta_link, '_blank')}
                    className="w-full bg-slate-900 text-white hover:bg-slate-800"
                    data-testid="sidebar-promo-cta"
                  >
                    {sidebarPromo.cta_text || 'En savoir plus'}
                  </Button>
                )}
              </CardContent>
            </Card>
          )}

          {/* Quick Actions — contextual, stretches to fill sidebar */}
          <Card className="bg-white border border-slate-200 shadow-md rounded-2xl flex-1 flex flex-col">
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Actions rapides</CardTitle>
            </CardHeader>
            <CardContent className="pt-0 flex-1 flex flex-col gap-3">
              {(() => {
                const actions = [];
                const hasPending = (stats.counters.pending || 0) > 0;
                const hasConnected = (stats.equipments.connected || 0) > 0;
                const allAutomated = !hasPending && hasConnected;
                const hasUnpaid = (stats.invoices.unpaid || 0) > 0;

                // Payer une facture (priority if unpaid)
                if (hasUnpaid) {
                  actions.push({
                    key: 'pay',
                    icon: <CreditCard className="w-4 h-4 mr-2 text-rose-500" />,
                    label: `Régler mes factures (${stats.invoices.unpaid})`,
                    onClick: () => navigate('/espace-client/factures'),
                    highlight: true,
                  });
                }

                // Saisir un compteur — only if MPS contract AND manual entry still needed
                if (stats.contracts.hasMps && !allAutomated) {
                  actions.push({
                    key: 'counter',
                    icon: <Gauge className="w-4 h-4 mr-2 text-amber-500" />,
                    label: 'Saisir un compteur',
                    onClick: () => navigate('/espace-client/compteurs'),
                  });
                }

                // Créer un ticket
                actions.push({
                  key: 'ticket',
                  icon: <Ticket className="w-4 h-4 mr-2 text-purple-500" />,
                  label: 'Créer un ticket support',
                  onClick: () => navigate('/espace-client/tickets'),
                });

                // Voir mes factures
                actions.push({
                  key: 'invoices',
                  icon: <Euro className="w-4 h-4 mr-2 text-emerald-500" />,
                  label: 'Mes factures',
                  onClick: () => navigate('/espace-client/factures'),
                });

                // Mes contrats (if any)
                if (stats.contracts.total > 0) {
                  actions.push({
                    key: 'contracts',
                    icon: <FileSignature className="w-4 h-4 mr-2 text-indigo-500" />,
                    label: 'Mes contrats',
                    onClick: () => navigate('/espace-client/contrats'),
                  });
                }

                // Mes équipements (if any)
                if (stats.equipments.total > 0) {
                  actions.push({
                    key: 'equipments',
                    icon: <Printer className="w-4 h-4 mr-2 text-slate-500" />,
                    label: 'Mes équipements',
                    onClick: () => navigate('/espace-client/equipements'),
                  });
                }

                // Mes documents
                actions.push({
                  key: 'documents',
                  icon: <FolderOpen className="w-4 h-4 mr-2 text-blue-500" />,
                  label: 'Mes documents',
                  onClick: () => navigate('/espace-client/documents'),
                });

                return actions.map((a) => (
                  <Button
                    key={a.key}
                    variant="outline"
                    className={`w-full justify-start ${
                      a.highlight ? 'border-rose-200 bg-rose-50/50 hover:bg-rose-50' : ''
                    }`}
                    onClick={a.onClick}
                  >
                    {a.icon}
                    {a.label}
                  </Button>
                ));
              })()}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default PortalDashboardPage;
